import { ItemsPerPage } from '@billy/design-system'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { usePeriodDates } from '../../../hooks/usePeriodDates'
import { Paging } from '../../../types/metable'
import { quoteListFilterToApiParams, resolveStatusParam } from '../constants/quoteListFilterToApiParams'
import { InvoiceStatus } from '../enums/invoiceStatus'
import { QuoteListFilter } from '../enums/quoteListFilter'
import { fetchSalesDocuments } from '../query-api'
import { FetchSalesDocumentsParams } from '../types/salesDocument/fetchSalesDocumentsParams'
import { SalesDocumentPropsByListingType } from '../types/salesDocument/salesDocumentPropsByListingType'
import { SalesListingDocumentType } from '../types/salesDocument/salesListingDocumentType'

type UseFetchSalesDocumentsProps<T extends SalesListingDocumentType> = FetchSalesDocumentsParams<T> & {
  period?: string
  documentNumberPrefix?: string
}

const SalesDocumentListQueryKey: Record<SalesListingDocumentType, QueryKeys> = {
  invoices: QueryKeys.InvoicesZervant,
  quotes: QueryKeys.Quotes,
}

const CustomerSalesDocumentToQueryKey: Record<SalesListingDocumentType, QueryKeys> = {
  invoices: QueryKeys.ClientInvoicesZervant,
  quotes: QueryKeys.ClientQuotes,
}

export const useFetchSalesDocuments = <T extends SalesListingDocumentType>({
  customerId,
  documentsListType,
  documentNumber: fullDocumentNumber,
  documentNumberPrefix,
  documentTypes: documentTypesOverrides,
  isEnabled = true,
  page,
  pageSize,
  period,
  searchQuery,
  sortDirection,
  sortProperty,
  status,
}: UseFetchSalesDocumentsProps<T>) => {
  const previousPagination = useRef<Paging | undefined>(undefined)
  const { isLoading: isLoadingPeriodDates, fromDate, toDate } = usePeriodDates(period)

  // TODO: refactor the params creation part and adjust typing
  const queryParams = useMemo(() => {
    type Status = SalesDocumentPropsByListingType[T]['status']
    type DocumentType = SalesDocumentPropsByListingType[T]['documentTypes']
    let resolvedStatus: Status | undefined = status
    let documentTypes: DocumentType | undefined = documentTypesOverrides

    if (documentsListType === 'invoices') {
      if (status === InvoiceStatus.All) {
        resolvedStatus = undefined
      }
    } else if (documentsListType === 'quotes') {
      const apiParams = quoteListFilterToApiParams[status as QuoteListFilter]
      documentTypes = documentTypes || (apiParams?.documentTypes as DocumentType | undefined)
      resolvedStatus = resolveStatusParam(apiParams?.status) as Status | undefined
    }

    // if documentNumberPrefix is provided, we can use it to filter by it within documentNumber (PREFIX-%)
    const documentNumberPrefixFilter = documentNumberPrefix ? `${documentNumberPrefix}-` : undefined
    // full specific documentNumber is more important than prefix
    const documentNumber = fullDocumentNumber || documentNumberPrefixFilter

    return {
      ...(customerId ? { customerId } : {}),
      ...(documentNumber ? { documentNumber } : {}),
      ...(documentTypes ? { documentTypes } : {}),
      documentsListType,
      ...(fromDate && toDate ? { fromDate, toDate } : {}),
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize: pageSize as ItemsPerPage } : {}),
      ...(searchQuery ? { searchQuery } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
      ...(resolvedStatus ? { status: resolvedStatus } : {}),
    }
  }, [
    customerId,
    documentNumberPrefix,
    documentTypesOverrides,
    documentsListType,
    fromDate,
    fullDocumentNumber,
    page,
    pageSize,
    searchQuery,
    sortDirection,
    sortProperty,
    status,
    toDate,
  ])

  const queryKey = useMemo(
    () => [
      customerId ? CustomerSalesDocumentToQueryKey[documentsListType] : SalesDocumentListQueryKey[documentsListType],
      queryParams,
    ],
    [customerId, documentsListType, queryParams],
  )

  const {
    data,
    isFetched,
    isFetching,
    isLoading: isLoadingQuery,
    isSuccess,
  } = useQuery(queryKey, () => fetchSalesDocuments(queryParams), {
    enabled: !isLoadingPeriodDates && isEnabled,
    keepPreviousData: true,
    staleTime: defaultStaleTime,
  })

  // If pagination exists then copy the value to reference and use it when original pagination during loading returns `undefined` value.
  useEffect(() => {
    if (data?.data?.meta.paging) {
      previousPagination.current = { ...data?.data?.meta.paging }
    }
  }, [data?.data?.meta.paging])

  const isLoading = isLoadingQuery || isLoadingPeriodDates

  const documents = useMemo(() => data?.data?.documents || [], [data?.data?.documents])

  return {
    documents,
    pagination: data?.data.meta.paging || previousPagination.current,
    isFetched,
    isFetching,
    isLoading,
    isSuccess,
  }
}
